import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BellRing, ClipboardCheck, SendHorizonal, Undo2, X } from 'lucide-react';
import { cn } from '../../lib/cn';
import { formatDateShort } from '../../lib/format';
import { usePeople, useTimeAttention, type PeriodKind } from './api';
import { addDaysIso, formatHoursUnit, type HoursFormat } from './hours';

const KIND_WORD: Record<PeriodKind, string> = { DAY: 'day', WEEK: 'week', MONTH: 'month' };

/** Last day of a period, from its kind and first day (calendar arithmetic, no UTC). */
function periodEnd(p: { periodKind: PeriodKind; periodStart: string }): string {
  if (p.periodKind === 'DAY') return p.periodStart;
  if (p.periodKind === 'WEEK') return addDaysIso(p.periodStart, 6);
  const [y, m] = p.periodStart.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${p.periodStart.slice(0, 8)}${String(last).padStart(2, '0')}`;
}

/** "the week of Aug 10 – 16, 2026" · "August 2026" — for a sentence. */
function nounOf(p: { periodKind: PeriodKind; label: string }): string {
  return p.periodKind === 'WEEK' ? `the week of ${p.label}` : p.label;
}

/**
 * ⚠ **THE TIMESHEET ONLY EVER KNEW ABOUT THE WEEK ON SCREEN.**
 *
 * A week an approver sent back, four sheets waiting on your signature and six
 * past weeks nobody ever submitted all sat one arrow-click away, and the screen
 * mentioned none of them. Each one ends in the same place: hours that are never
 * invoiced. This is the one place the app says so, and it says it where the
 * person already is — no new screen, no nav item, no extra click to discover
 * that something is wrong.
 *
 * THREE RULES IT MUST KEEP:
 *
 *  1. **Silent on a clean week.** The ordinary case renders `null`. Somebody
 *     logging time on a week where nothing is outstanding sees exactly what
 *     they saw before this existed.
 *  2. **Never restate the screen.** Anything about the week currently open is
 *     dropped — `WeekApprovalBar` already carries the rejection reason and the
 *     Submit button for THAT week. The strip exists for what is out of view,
 *     which is precisely what the defect was.
 *  3. **Every chip is a real link.** `href` is the shareable URL, so a chip is
 *     tab-reachable and cmd-clickable; a plain click is intercepted so the page
 *     moves in place (the anchor day lives in TimePage state, and letting the
 *     router change the URL underneath it would be rewritten back a tick later).
 */

const DISMISS_KEY = 'financials-time-attention-dismissed';

function readDismissed(): string | null {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY);
  } catch {
    return null;
  }
}

export type AttentionTarget = 'own' | 'approvals';

interface Chip {
  key: string;
  href: string;
  target: AttentionTarget;
  periodStart: string;
  icon: typeof Undo2;
  label: React.ReactNode;
  title: string;
  /** The rejection leads: it is the only one with a person's words attached. */
  loud?: boolean;
}

export function AttentionStrip({
  /**
   * First and last day the status bar above the sheet ACCOUNTS FOR — the week
   * in Week view, the one selected day in Day view (whose bar lists only the
   * period governing that day). Measured against the week in Day view, a
   * monthly contractor on Sep 1 had August suppressed here and absent from the
   * day's bar: outstanding, and said nowhere.
   */
  displayedStart,
  displayedEnd,
  /** Which of the three views is open — Approvals is about everyone. */
  view,
  /** False while an approver is reading somebody else's sheet. */
  viewingOwnSheet,
  hoursFormat,
  /** Move the page to a week in place (state, not a router navigation). */
  onGoTo,
  className,
}: {
  displayedStart: string;
  displayedEnd: string;
  view: 'week' | 'day' | 'approvals';
  viewingOwnSheet: boolean;
  hoursFormat: HoursFormat;
  onGoTo: (periodStart: string, target: AttentionTarget) => void;
  className?: string;
}) {
  const attention = useTimeAttention();
  const people = usePeople();
  const ownPersonId = people.data?.viewer.personId ?? null;
  const [dismissed, setDismissed] = useState<string | null>(readDismissed);

  const data = attention.data;

  const chips = useMemo<Chip[]>(() => {
    if (!data) return [];
    // The same place a plain click goes: a click keeps the Day view, so a new
    // tab opened from the chip does too.
    const ownHref = (start: string) =>
      `/time?view=${view === 'day' ? 'day' : 'week'}&date=${start}${
        ownPersonId ? `&person=${ownPersonId}` : ''
      }`;
    /**
     * Is this period already on screen, as its owner? A timesheet is a PERIOD
     * (a day, a week or a month), so "on screen" means the period overlaps the
     * range the status bar above the sheet lists — the week, or in Day view the
     * one day — and that bar already carries its status and its Submit.
     */
    const onScreenAsOwner = (p: { periodKind: PeriodKind; periodStart: string }) =>
      view !== 'approvals' &&
      viewingOwnSheet &&
      p.periodStart <= displayedEnd &&
      periodEnd(p) >= displayedStart;

    const out: Chip[] = [];

    for (const period of data.rejected) {
      if (onScreenAsOwner(period)) continue;
      out.push({
        key: `rejected:${period.periodKind}:${period.periodStart}`,
        href: ownHref(period.periodStart),
        target: 'own',
        periodStart: period.periodStart,
        icon: Undo2,
        loud: true,
        label: (
          <>
            {period.periodKind === 'WEEK' ? 'Your week of ' : 'Your '}
            <span className="whitespace-nowrap">{period.label}</span> was sent back
          </>
        ),
        title:
          `${period.reviewedBy ?? 'An approver'} sent ${nounOf(period)} back to you` +
          `${period.reviewedAt ? ` on ${formatDateShort(period.reviewedAt)}` : ''}.` +
          (period.reviewNote ? `\n\n“${period.reviewNote}”` : ''),
      });
    }

    const unsubmitted = data.unsubmitted.periods.filter((p) => !onScreenAsOwner(p));
    if (unsubmitted.length > 0) {
      const hours = unsubmitted.reduce((sum, p) => sum + p.hours, 0);
      const oldest = unsubmitted[0];
      const word = KIND_WORD[oldest.periodKind];
      out.push({
        key: 'unsubmitted',
        href: ownHref(oldest.periodStart),
        target: 'own',
        periodStart: oldest.periodStart,
        icon: SendHorizonal,
        label: (
          <>
            {unsubmitted.length === 1
              ? oldest.periodKind === 'WEEK'
                ? '1 week'
                : oldest.label
              : `${unsubmitted.length} ${word}s`}{' '}
            unsubmitted · <span className="num">{formatHoursUnit(hours, hoursFormat)}</span>
          </>
        ),
        title:
          `Tracked but never sent for approval, in the last ${data.lookback} ${word}s. ` +
          `Opens the oldest, ${oldest.label}.\n\n` +
          unsubmitted
            .map((p) => `${p.label} — ${formatHoursUnit(p.hours, hoursFormat)}`)
            .join('\n'),
      });
    }

    /**
     * ⚠ NEVER RESTATE THE SCREEN. Approvals is the queue of every submitted
     * period, so on that view this chip would be the list below it, twice.
     */
    const awaiting = view === 'approvals' ? [] : data.awaiting.periods;
    const awaitingCount = awaiting.reduce((sum, p) => sum + p.count, 0);
    if (awaitingCount > 0) {
      const oldest = awaiting[0];
      out.push({
        key: 'awaiting',
        href: `/time?view=approvals&date=${oldest.periodStart}`,
        target: 'approvals',
        periodStart: oldest.periodStart,
        icon: ClipboardCheck,
        label: (
          <>
            <span className="num">{awaitingCount}</span>{' '}
            {awaitingCount === 1 ? 'sheet' : 'sheets'} awaiting you
          </>
        ),
        title:
          `Submitted and waiting for your approval. Opens the Approvals queue.\n\n` +
          awaiting.map((p) => `${p.label} — ${p.people.join(', ')}`).join('\n'),
      });
    }

    return out;
  }, [data, displayedStart, displayedEnd, view, viewingOwnSheet, ownPersonId, hoursFormat]);

  /**
   * Dismissal is keyed to WHAT it said, not to the strip. Hiding it once must
   * not hide the next rejection, and a week you finally submit should take its
   * own chip away rather than leaving the reader to trust a stale bar.
   */
  const signature = useMemo(() => chips.map((c) => `${c.key}:${c.periodStart}`).join('|'), [chips]);

  useEffect(() => {
    // ⚠ NOT UNTIL THE FACTS HAVE LOADED. On the first render of a reload the
    // query is still in flight, so the signature is the empty string — comparing
    // against it re-armed the strip on every refresh and made "dismiss" last
    // exactly one render. A signature that is genuinely no longer showing is
    // dead weight in storage, and clearing it is what stops a dismissal from
    // leaking onto a different set of facts.
    if (!data) return;
    if (dismissed && dismissed !== signature) setDismissed(null);
  }, [data, dismissed, signature]);

  if (chips.length === 0 || dismissed === signature) return null;

  const dismiss = () => {
    try {
      window.sessionStorage.setItem(DISMISS_KEY, signature);
    } catch {
      /* private mode — it just comes back on the next render */
    }
    setDismissed(signature);
  };

  return (
    <div
      className={cn(
        'mb-3 flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-xl border border-warning/40 bg-warning/10 px-3 py-2 sm:px-4',
        className,
      )}
      role="status"
    >
      <span className="flex items-center gap-1.5 text-body-sm font-bold leading-snug text-secondary">
        <BellRing className="h-4 w-4 shrink-0 text-status-partial" aria-hidden />
        Needs you
      </span>
      {chips.map((chip) => (
        <Link
          key={chip.key}
          to={chip.href}
          title={chip.title}
          onClick={(event) => {
            // Let cmd/ctrl/shift/middle click open the real URL in a new tab.
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
            event.preventDefault();
            onGoTo(chip.periodStart, chip.target);
          }}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 text-caption font-bold leading-tight transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-status-partial focus-visible:ring-offset-1',
            chip.loud
              ? 'border-status-partial/50 bg-white text-status-partial hover:bg-warning/20'
              : 'border-warning/40 bg-white/70 text-secondary hover:bg-white',
          )}
        >
          <chip.icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{chip.label}</span>
          <span aria-hidden>→</span>
        </Link>
      ))}
      <span className="text-caption leading-tight text-grey-500">
        {view === 'approvals' ? 'your own timesheets' : `beyond this ${view === 'day' ? 'day' : 'week'}`}
      </span>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss until something changes"
        title="Dismiss until something changes"
        className="ml-auto shrink-0 rounded-md p-1 text-grey-500 transition-colors hover:bg-warning/20 hover:text-secondary"
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );
}
